// What changed between two saved versions of a record, field by field, for the history panel
import { plainText } from "./search/sections"

export interface FieldChange {
  label: string
  before: string
  after: string
}

export interface DiffPart {
  kind: "same" | "added" | "removed"
  text: string
}

const SKIP = new Set(["id", "createdAt", "updatedAt", "locale", "translations"])

const humanize = (k: string) =>
  k.replace(/Ids?$/, "").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ").replace(/^./, c => c.toUpperCase())

// A value as readable text: HTML as its words, lists and objects compactly
function show(v: unknown): string {
  if (v == null || v === "") return ""
  if (typeof v === "string") return /<[a-z][\s\S]*>/i.test(v) ? plainText(v) : v
  if (typeof v === "boolean") return v ? "Yes" : "No"
  if (typeof v === "number") return String(v)
  if (Array.isArray(v)) return v.map(show).filter(Boolean).join(", ")
  return Object.entries(v as Record<string, unknown>)
    .map(([k, x]) => (show(x) ? `${humanize(k)}: ${show(x)}` : ""))
    .filter(Boolean)
    .join(" · ")
}

export function fieldChanges(before: Record<string, any> | undefined, after: Record<string, any> | undefined): FieldChange[] {
  const a = before ?? {}
  const b = after ?? {}
  const out: FieldChange[] = []
  for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
    if (SKIP.has(key)) continue
    const x = show(a[key])
    const y = show(b[key])
    if (x !== y) out.push({ label: humanize(key), before: x, after: y })
  }
  const ta = a.translations ?? {}
  const tb = b.translations ?? {}
  for (const loc of new Set([...Object.keys(ta), ...Object.keys(tb)])) {
    for (const key of new Set([...Object.keys(ta[loc] ?? {}), ...Object.keys(tb[loc] ?? {})])) {
      if (SKIP.has(key)) continue
      const x = show(ta[loc]?.[key])
      const y = show(tb[loc]?.[key])
      if (x !== y) out.push({ label: `${humanize(key)} · ${loc}`, before: x, after: y })
    }
  }
  return out
}

// Word-level diff (longest common subsequence). Long texts fall back to before/after as a whole
export function wordDiff(before: string, after: string): DiffPart[] {
  const a = before.split(/(\s+)/)
  const b = after.split(/(\s+)/)
  if (a.length * b.length > 400_000) {
    return [
      ...(before ? [{ kind: "removed" as const, text: before }] : []),
      ...(after ? [{ kind: "added" as const, text: after }] : []),
    ]
  }
  const dp: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0))
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
    }
  }
  const out: DiffPart[] = []
  const push = (kind: DiffPart["kind"], text: string) => {
    const last = out[out.length - 1]
    if (last && last.kind === kind) last.text += text
    else out.push({ kind, text })
  }
  let i = 0
  let j = 0
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { push("same", a[i]); i++; j++ }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { push("removed", a[i]); i++ }
    else { push("added", b[j]); j++ }
  }
  while (i < a.length) push("removed", a[i++])
  while (j < b.length) push("added", b[j++])
  return out
}
